//
// Copyright (c) 2011-2026 by Curalate, Inc.
//

import { ICuralateSession } from 'app/basic-session/ICuralateSession';
import { crl8 } from 'app/curalate.globals';
import { SessionMode } from 'app/moderation-mode/constants/SessionMode';
import {
  deriveSessionMode,
  getIsModerationMergeEnabled,
  getIsModeratorCapable,
} from 'app/moderation-mode/moderationModeDerivation';
import { readStoredMode } from 'app/moderation-mode/moderationModeStorage';

/**
 * Impure boundary: reads the live session and persisted mode.
 * Everything downstream of this (reducers, derivation) stays pure.
 */
export const resolveSessionMode = (
  session: ICuralateSession | undefined = crl8.session,
): SessionMode => {
  if (!session) {
    return SessionMode.Client;
  }

  return deriveSessionMode({
    hasMergeEnabled: getIsModerationMergeEnabled(session),
    permissions: session.permissions,
    storedMode: readStoredMode(
      String(session.user.id),
      String(session.client.id),
    ),
  });
};

/**
 * Defence in depth at the dispatch boundary: a requested Moderator mode is
 * downgraded to Client unless the live session actually permits it.
 */
export const resolveRequestedSessionMode = (
  requested: SessionMode,
  session: ICuralateSession | undefined = crl8.session,
): SessionMode => {
  if (requested !== SessionMode.Moderator) {
    return requested;
  }

  const isAllowed =
    !!session &&
    getIsModerationMergeEnabled(session) &&
    getIsModeratorCapable(session.permissions);

  return isAllowed ? SessionMode.Moderator : SessionMode.Client;
};
